import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"
import { positionPanel, trackPosition } from "../utils/popover_panel"

// The Bezeichnung cell of a Kalkulation row in the position grid
// (position_grid/editors/_component_picker): what is typed is searched in the Artikelstamm
// and the Lohngruppen (Billing::LineItems::CalculationComponentsController#suggestions), and the
// hits are offered under the cell.
//
// The editor stays grid-cells' text editor: grid-cells saves it on Enter, Tab or leaving the cell.
// This controller only decides what goes along. ↑/↓ highlight a hit; Enter or Tab on a highlighted
// one — or a click — takes it: its name becomes the text, and the hidden `reference` tells the save
// to take the master data over with it (Costing::ComponentReference). Typing again drops a pick and
// the highlight: the text alone is just a Bezeichnung. Esc closes the list first, and only a second
// Esc leaves the editor (grid-cells ignores an Escape that was already handled). A closed list stays
// closed: a search still under way is dropped with it. A refused search (a locked document, a lost
// permission) shows the server's message instead of an empty list.
//
// Opened by Enter or F2, the stored Bezeichnung is not searched; opened by typing over it, the
// typed text is.
const DEBOUNCE = 200

export default class extends Controller {
  static targets = ["input", "reference", "list"]
  static values = { url: String, empty: String }

  connect() {
    this.suggestions = []
    this.highlighted = -1
    this.open = false
    this.request = 0
    // grid-cells inserts the editor, puts the typed character in and focuses it in one go — before
    // Stimulus connects this controller, so its `focus` action never saw that focus.
    if (document.activeElement === this.inputTarget) this.typedOver()
  }

  disconnect() {
    this.close()
  }

  // grid-cells puts a typed-over character into the input and then focuses it — there is no
  // `input` event for that first key.
  typedOver() {
    if (this.inputTarget.value !== this.inputTarget.defaultValue) this.search()
  }

  search() {
    this.referenceTarget.value = ""
    this.referenceTarget.removeAttribute("data-cell-changed")
    this.highlighted = -1
    this.markHighlighted()
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.load(this.inputTarget.value.trim()), DEBOUNCE)
  }

  async load(term) {
    const request = ++this.request
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", term)
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
      // An answer to an earlier keystroke, or to one before the list was closed, arriving late is
      // not what the cell shows now.
      const stale = () => request !== this.request || !this.inputTarget.isConnected
      if (stale()) return
      if (!response.ok) return this.refused(response)

      const suggestions = await response.json()
      if (stale()) return

      this.suggestions = suggestions
      this.highlighted = -1
      this.render()
    } catch (error) {
      Honeybadger.notify(error, { context: { url: this.urlValue } })
    }
  }

  // Billing::LineItems::CalculationComponentsController answers a refusal with the grid's flash.
  async refused(response) {
    this.close()
    if (response.headers.get("Content-Type")?.includes("turbo-stream")) {
      Turbo.renderStreamMessage(await response.text())
    }
  }

  keydown(event) {
    if (!this.open) {
      // A search still pending would open the list over a cell that is being saved or left.
      if (["Enter", "Tab", "Escape"].includes(event.key)) this.close()
      return
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      this.move(event.key === "ArrowDown" ? 1 : -1)
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if ((event.key === "Enter" || event.key === "Tab") && this.highlighted >= 0) {
      // Not prevented: grid-cells goes on to save the cell, with the pick in it.
      this.pick(this.highlighted)
    } else if (event.key === "Enter" || event.key === "Tab") {
      this.close()
    }
  }

  move(step) {
    if (this.suggestions.length === 0) return

    const count = this.suggestions.length
    this.highlighted = (this.highlighted + step + count) % count
    this.markHighlighted()
  }

  pick(index) {
    const suggestion = this.suggestions[index]
    if (!suggestion) return

    this.inputTarget.value = suggestion.label
    this.referenceTarget.value = suggestion.reference
    // Saved even where the name is the text already there: the master data comes with it.
    this.referenceTarget.setAttribute("data-cell-changed", "")
    this.close()
  }

  // A click picks and saves, as Enter on the highlighted hit does. The mousedown keeps the focus in
  // the input: leaving it would save the cell before the click lands.
  choose(event) {
    const index = Number(event.currentTarget.dataset.index)
    this.pick(index)
    this.inputTarget.dispatchEvent(
      new KeyboardEvent("keydown", { key: "Enter", bubbles: true, cancelable: true }),
    )
  }

  render() {
    const list = this.listTarget
    list.replaceChildren(
      ...(this.suggestions.length > 0
        ? this.suggestions.map((suggestion, index) => this.option(suggestion, index))
        : [this.emptyNote()]),
    )
    if (!this.open) {
      list.showPopover()
      this.open = true
      this.untrack = trackPosition(() => positionPanel(this.inputTarget, list))
      this.inputTarget.setAttribute("aria-expanded", "true")
    }
    positionPanel(this.inputTarget, list)
  }

  option(suggestion, index) {
    const item = document.createElement("li")
    item.id = `${this.listTarget.id}_${index}`
    item.setAttribute("role", "option")
    item.dataset.index = index
    item.className =
      "flex cursor-pointer items-baseline gap-2 rounded px-2 py-1.5 text-gray-800 aria-selected:bg-emerald-50"
    item.addEventListener("mousedown", (event) => event.preventDefault())
    item.addEventListener("click", (event) => this.choose(event))

    const kind = document.createElement("span")
    kind.className = "w-20 shrink-0 text-[11px] text-gray-400"
    kind.textContent = suggestion.kind
    const label = document.createElement("span")
    label.className = "min-w-0 flex-1 truncate"
    label.textContent = suggestion.label
    const detail = document.createElement("span")
    detail.className = "shrink-0 text-xs tabular-nums text-gray-500"
    detail.textContent = suggestion.detail
    item.append(kind, label, detail)
    return item
  }

  emptyNote() {
    const note = document.createElement("li")
    note.className = "px-2 py-1.5 text-xs text-gray-500"
    note.textContent = this.emptyValue
    return note
  }

  markHighlighted() {
    this.listTarget.querySelectorAll("[role=option]").forEach((item, index) => {
      item.setAttribute("aria-selected", index === this.highlighted ? "true" : "false")
      if (index === this.highlighted) item.scrollIntoView({ block: "nearest" })
    })
    const active = this.listTarget.querySelector('[aria-selected="true"]')
    if (active) this.inputTarget.setAttribute("aria-activedescendant", active.id)
    else this.inputTarget.removeAttribute("aria-activedescendant")
  }

  close() {
    clearTimeout(this.timer)
    this.request++
    this.untrack?.()
    this.untrack = null
    if (this.hasListTarget && this.open) this.listTarget.hidePopover()
    this.open = false
    if (this.hasInputTarget) {
      this.inputTarget.setAttribute("aria-expanded", "false")
      this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }
}
